import { Link } from '@tanstack/react-router';
import { Ban, CheckCircle2, Download, MoreHorizontal, PlayCircle } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { PrivacyRequest } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';

import {
  canDownloadExport,
  nextStatuses,
  type PrivacyRequestMove,
} from '../schemas/privacy-request-rules';

import { PrivacyRequestStatusBadge } from './privacy-request-status-badge';

export type PrivacyRequestAction = PrivacyRequestMove | 'download';

const MOVE_ICON = { in_progress: PlayCircle, completed: CheckCircle2, rejected: Ban } as const;

/** An anonymised customer answers 404, so a completed deletion no longer links to it. */
function linksToCustomer(request: PrivacyRequest): request is PrivacyRequest & {
  customerId: string;
} {
  return (
    request.customerId !== null && !(request.kind === 'delete' && request.status === 'completed')
  );
}

/** Column model of the privacy requests table; `meta` ties columns to the list's sorts and filters. */
export function usePrivacyRequestColumns(
  storeId: string,
  onAction: (action: PrivacyRequestAction, request: PrivacyRequest) => void,
) {
  const { t } = useTranslation('privacyRequests');
  const format = useFormat();
  const { can } = usePermissions();
  const canWrite = can('customers.write');

  return useMemo<Array<DataTableColumnDef<PrivacyRequest>>>(
    () => [
      {
        id: 'email',
        header: t('columns.email'),
        cell: ({ row }) =>
          linksToCustomer(row.original) ? (
            <Link
              to="/s/$storeId/customers/$customerId"
              params={{ storeId, customerId: row.original.customerId }}
              className="font-medium break-all hover:underline"
            >
              {row.original.email}
            </Link>
          ) : (
            <span className="font-medium break-all">{row.original.email}</span>
          ),
        meta: { label: t('columns.email'), filterKey: 'customerId', lockVisibility: true },
      },
      {
        id: 'kind',
        header: t('columns.kind'),
        cell: ({ row }) => t(`kind.${row.original.kind}`),
        meta: { label: t('columns.kind'), filterKey: 'kind' },
        size: 150,
      },
      {
        id: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <PrivacyRequestStatusBadge status={row.original.status} />,
        meta: { label: t('columns.status'), filterKey: 'status' },
        size: 120,
      },
      {
        id: 'requestedAt',
        header: t('columns.requested'),
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={format.utc(row.original.requestedAt)}>
            {format.relative(row.original.requestedAt)}
          </span>
        ),
        meta: { label: t('columns.requested'), sortField: 'requestedAt', filterKey: 'requestedAt' },
        size: 130,
      },
      {
        id: 'completedAt',
        header: t('columns.completed'),
        cell: ({ row }) =>
          row.original.completedAt ? (
            <span className="text-muted-foreground" title={format.utc(row.original.completedAt)}>
              {format.date(row.original.completedAt)}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
        meta: { label: t('columns.completed') },
        size: 130,
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">{t('columns.actions')}</span>,
        meta: { label: t('columns.actions'), align: 'end' },
        cell: ({ row }) => {
          const moves = canWrite ? nextStatuses(row.original.status) : [];
          const download = canDownloadExport(row.original);
          if (moves.length === 0 && !download) return null;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('actions.menu', { email: row.original.email })}
                >
                  <MoreHorizontal aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {moves.map((move) => {
                  const Icon = MOVE_ICON[move];
                  return (
                    <DropdownMenuItem
                      key={move}
                      variant={move === 'rejected' ? 'destructive' : 'default'}
                      onSelect={() => onAction(move, row.original)}
                    >
                      <Icon aria-hidden />
                      {t(`actions.${move}`)}
                    </DropdownMenuItem>
                  );
                })}
                {moves.length > 0 && download ? <DropdownMenuSeparator /> : null}
                {download ? (
                  <DropdownMenuItem onSelect={() => onAction('download', row.original)}>
                    <Download aria-hidden />
                    {t('actions.download')}
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        size: 56,
      },
    ],
    [canWrite, format, onAction, storeId, t],
  );
}
